import { useMutation, gql } from '@apollo/client';
import { useStorage } from '~Components/Storage';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';

const UPDATE_CUSTOMER_CONNECTED_RECEIPT_COUNTER = gql`
  mutation UpdateCustomerConnectedReceiptCounter($id: String!) {
    updateCustomerConnectedReceiptCounter(id: $id) {
      success
      message
      error
      count
    }
  }
`;

const UPDATE_REFUND_RECEIPT_COUNTER = gql`
  mutation UpdateRefundReceiptCounter($id: String!) {
    updateRefundReceiptCounter(id: $id) {
      success
      message
      error
      count
    }
  }
`;

const UPDATE_RECEIPT_COUNTER = gql`
  mutation UpdateReceiptCounter($id: String!) {
    updateReceiptCounter(id: $id) {
      success
      message
      error
      count
    }
  }
`;

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 2000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const useDeviceId = () => {
  const deviceHandler = useDeviceHandler();
  const [storage] = useStorage();
  const companyFlags = useSelector((state) => state.companyFlags);

  const shouldUseNewWay = companyFlags.useNewLicenses && !deviceHandler?.activeDevice?.isMigrated;

  return shouldUseNewWay ? deviceHandler?.activeDevice?.id || storage.deviceId : storage.deviceId;
};

/**
 * Atomically increments the receipt counter on the backend.
 * Returns the new count. Retries up to 5 times.
 * Updates Redux so all consumers (OrderSyncer, usePrintReceipt) see the correct value.
 */
export const useIncrementReceiptCounter = () => {
  const [updateCounter] = useMutation(UPDATE_RECEIPT_COUNTER);
  const deviceId = useDeviceId();
  const dispatch = useDispatch();
  const logger = useLogger('receipt-counter');

  return async (): Promise<number> => {
    try {
      if (!deviceId) {
        logger.error('No device ID found when trying to increment receipt counter');
        throw new Error('No device ID found');
      }

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          logger.info('Incrementing receipt counter via backend', { deviceId, attempt });
          const { data } = await updateCounter({ variables: { id: deviceId } });

          if (!data?.updateReceiptCounter?.success) {
            throw new Error(
              data?.updateReceiptCounter?.error ||
                data?.updateReceiptCounter?.message ||
                'Failed to increment receipt counter',
            );
          }

          const newCount = data.updateReceiptCounter.count;
          logger.info('Receipt counter incremented', { newCount, deviceId });

          dispatch({ type: actions.MESSAGE_RECEIPT_COUNTER, receiptCounter: newCount });
          window.postMessageBridge({ receiptCounter: newCount });

          return newCount;
        } catch (e) {
          logger.warn(`Receipt counter attempt ${attempt}/${MAX_RETRIES} failed`, {
            errorMsg: e instanceof Error ? e.message : String(e),
            deviceId,
          });
          if (attempt < MAX_RETRIES) {
            logger.info(`Retrying receipt counter increment (attempt ${attempt + 1})`, {
              deviceId,
            });
            await wait(RETRY_DELAY_MS);
          } else {
            throw e;
          }
        }
      }

      throw new Error('Failed to increment receipt counter');
    } catch (e) {
      logger.error('Fatal: receipt counter increment failed', {
        errorMsg: e instanceof Error ? e.message : String(e),
        deviceId,
      });
      throw e;
    }
  };
};

/**
 * Atomically increments the refund receipt counter on the backend.
 * Returns the receipt number
 * Separate counter from regular receipts.
 */
export const useIncrementRefundReceiptCounter = () => {
  const [updateCounter] = useMutation(UPDATE_REFUND_RECEIPT_COUNTER);
  const deviceId = useDeviceId();
  const logger = useLogger('refund-receipt-counter');

  return async (): Promise<number> => {
    try {
      if (!deviceId) {
        logger.error('No device ID found when trying to increment refund receipt counter');
        throw new Error('No device ID found');
      }

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          logger.info('Incrementing refund receipt counter via backend', { deviceId, attempt });
          const { data } = await updateCounter({ variables: { id: deviceId } });

          if (!data?.updateRefundReceiptCounter?.success) {
            throw new Error(
              data?.updateRefundReceiptCounter?.error ||
                data?.updateRefundReceiptCounter?.message ||
                'Failed to increment refund receipt counter',
            );
          }

          const newCount = data.updateRefundReceiptCounter.count;

          logger.info('Refund receipt counter incremented', { newCount, deviceId });
          return parseInt(newCount, 10);
        } catch (e) {
          logger.warn(`Refund receipt counter attempt ${attempt}/${MAX_RETRIES} failed`, {
            errorMsg: e instanceof Error ? e.message : String(e),
            deviceId,
          });
          if (attempt < MAX_RETRIES) {
            logger.info(`Retrying refund receipt counter increment (attempt ${attempt + 1})`, {
              deviceId,
            });
            await wait(RETRY_DELAY_MS);
          } else {
            throw e;
          }
        }
      }

      throw new Error('Failed to increment refund receipt counter');
    } catch (e) {
      logger.error('Fatal: refund receipt counter increment failed', {
        errorMsg: e instanceof Error ? e.message : String(e),
        deviceId,
      });
      throw e;
    }
  };
};

/**
 * Atomically increments the customer-connected receipt counter on the backend.
 * Used in Spain when a receipt is linked to a customer (tax invoicing).
 * Returns the receipt number
 */
export const useIncrementCustomerConnectedReceiptCounter = () => {
  const [updateCounter] = useMutation(UPDATE_CUSTOMER_CONNECTED_RECEIPT_COUNTER);
  const deviceId = useDeviceId();
  const deviceHandler = useDeviceHandler();
  const dispatch = useDispatch();
  const logger = useLogger('customer-receipt-counter');

  return async (): Promise<{ count: number; formatted: string }> => {
    try {
      if (!deviceId) {
        logger.error(
          'No device ID found when trying to increment customer-connected receipt counter',
        );
        throw new Error('No device ID found');
      }

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          logger.info('Incrementing customer-connected receipt counter via backend', {
            deviceId,
            attempt,
          });
          const { data } = await updateCounter({ variables: { id: deviceId } });

          if (!data?.updateCustomerConnectedReceiptCounter?.success) {
            throw new Error(
              data?.updateCustomerConnectedReceiptCounter?.error ||
                data?.updateCustomerConnectedReceiptCounter?.message ||
                'Failed to increment customer-connected receipt counter',
            );
          }

          const newCount = data.updateCustomerConnectedReceiptCounter.count;
          const deviceName = deviceHandler?.activeDevice?.name || '';
          const formatted = `${deviceName}-${newCount}`;

          logger.info('Customer-connected receipt counter incremented', { formatted, deviceId });

          // Update Redux so usePrintReceipt reads the correct value
          dispatch({ type: actions.MESSAGE_RECEIPT_COUNTER, receiptCounter: newCount });
          window.postMessageBridge({ receiptCounter: newCount });

          return { count: newCount, formatted };
        } catch (e) {
          logger.warn(
            `Customer-connected receipt counter attempt ${attempt}/${MAX_RETRIES} failed`,
            {
              errorMsg: e instanceof Error ? e.message : String(e),
              deviceId,
            },
          );

          if (attempt < MAX_RETRIES) {
            logger.info(
              `Retrying customer-connected receipt counter increment (attempt ${attempt + 1})`,
              { deviceId },
            );
            await wait(RETRY_DELAY_MS);
          } else {
            throw e;
          }
        }
      }

      throw new Error('Failed to increment customer-connected receipt counter');
    } catch (e) {
      logger.error('Fatal: customer-connected receipt counter increment failed', {
        errorMsg: e instanceof Error ? e.message : String(e),
        deviceId,
      });
      throw e;
    }
  };
};

/**
 * Drop-in replacement for dispatch({ type: PRINT_RECEIPT, ... }).
 *
 * For Spain: calls the backend to increment the correct counter first, then dispatches
 * PRINT_RECEIPT with skipCounterIncrement so the reducer doesn't double-count.
 * Uses the customer-connected counter if the order has a userId, otherwise the regular counter.
 *
 * For other countries: dispatches PRINT_RECEIPT normally (reducer handles local increment).
 */
export const useDispatchPrintReceipt = () => {
  const dispatch = useDispatch();
  const country = useSelector((state) => state.country);
  const incrementReceiptCounter = useIncrementReceiptCounter();
  const incrementCustomerConnectedReceiptCounter = useIncrementCustomerConnectedReceiptCounter();
  const logger = useLogger('dispatch-print-receipt');
  const companyFlags = useSelector((state) => state.companyFlags);

  return async (action: Record<string, any>) => {
    if (country === 'ES' && companyFlags.useNewLicenses) {
      const hasCustomer = !!action.order?.userId;
      let skipCounterIncrement = false;

      try {
        if (hasCustomer) {
          await incrementCustomerConnectedReceiptCounter();
        } else {
          await incrementReceiptCounter();
        }
        skipCounterIncrement = true;
      } catch (e) {
        // Counter increment failed after all retries — still print the receipt
        logger.error('Receipt counter increment failed, printing receipt without counter update', {
          printError: e instanceof Error ? e.message : String(e),
          orderId: action.order?.id,
          hasCustomer,
        });
      }

      dispatch({
        ...action,
        type: actions.PRINT_RECEIPT,
        skipCounterIncrement: skipCounterIncrement,
      });
    } else {
      dispatch({ ...action, type: actions.PRINT_RECEIPT });
    }
  };
};
